import { Sidebar } from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatHeader, ChatListItem } from '../types/types';
import { GetChatHeader, MarkChatAsSeen } from '../services/chatServices';
import { useSignalR } from 'contexts/SignalRProvider';
import GroupeList from './GroupeList';
import CreateGroupe from './CreateGroupe';
import MainChatContent from './MainChatContent';
import UserList from './UserList';
import LucideIcon from 'components/common/LucideIcon';
import { ConversationSkeleton } from './ChatSkeletons';

interface IndexChatProps {
  userId?: string;
  isGroup?: boolean;
}

const IndexChat: React.FC<SideBarProps<IndexChatProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { connection } = useSignalR();
  const { t } = useTranslation();
  const { closeSidebar, openSidebar } = useSidebarManager();
  const [isGroup, setIsGroup] = useState<boolean>(params?.isGroup ?? false);
  const [headerInfo, setHeaderInof] = useState<ChatHeader | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [directCount, setDirectCount] = useState<number | null>(null);
  const [groupsCount, setGroupsCount] = useState<number | null>(null);
  const [lastOpenedId, setLastOpenedId] = useState<string | null>(null);
  const [resetGroupDetailsKey, setResetGroupDetailsKey] = useState(0);
  const [loadingConversation, setLoadingConversation] =
    useState<boolean>(false);

  const handelSelectUser = useCallback(
    async (userId: string, isGroup: boolean): Promise<void> => {
      const sectionMessage = document.querySelectorAll(
        '.minthr-chat-item-message',
      );

      if (sectionMessage) {
        sectionMessage.forEach((item) => {
          item.remove();
        });
      }
      setTimeout(() => {
        connection?.invoke('connectedUser', userId).catch(function (err) {
          return console.log(err.toString());
        });
      }, 300);

      setLoadingConversation(true);
      try {
        const headerRes = await GetChatHeader({
          id: userId,
          isgroup: isGroup,
          ids: [],
        });
        if (headerRes.success) {
          setHeaderInof(headerRes.data);
        } else {
          console.error(headerRes.message, 'Failed to fetch chat header');
        }

        const seenRes = await MarkChatAsSeen(userId, isGroup);
        if (!seenRes.success) {
          console.error(seenRes.message, 'Failed to mark chat as seen');
        }
      } finally {
        setLoadingConversation(false);
      }
    },
    [connection],
  );

  useEffect(() => {
    if (params?.userId) {
      void handelSelectUser(params?.userId, isGroup);
    }
  }, [params?.userId, isGroup, handelSelectUser, refresh]);

  const onSelectUser = async (user: ChatListItem): Promise<void> => {
    setIsGroup(user.isGroup);
    setLastOpenedId(user.chatUser.value);
    setResetGroupDetailsKey((prev) => prev + 1);
    await handelSelectUser(user.chatUser.value, user.isGroup);
  };

  const handleBackToList = (): void => {
    setHeaderInof(null);
  };

  const totalCount = (directCount ?? 0) + (groupsCount ?? 0) || null;

  return (
    <Sidebar
      size="chat"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
      className="tw-p-0 tw-overflow-hidden"
    >
      <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-bg-white">
        {!headerInfo ? (
          <>
            {/* === Panel header === */}
            <header
              className="tw-relative tw-px-[calc(var(--unit-minthr)*18)] tw-pt-3 tw-pb-2.5 "
              style={{
                background: 'linear-gradient(180deg, #fbfdfc 0%, #ffffff 100%)',
              }}
            >
              {/* Eyebrow */}
              <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[calc(var(--unit-minthr)*10)] tw-font-bold tw-uppercase tw-tracking-[0.16em] tw-text-primary">
                <span
                  className="tw-w-1 tw-h-1 tw-rounded-full tw-bg-primary"
                  style={{
                    boxShadow: '0 0 0 2px var(--minthr-color-primary-opacity)',
                  }}
                />
                {t('chat.workspace', { defaultValue: 'Espace de travail' })}
              </div>

              {/* Title */}
              <h2 className="tw-mt-1 tw-py-0.5 tw-text-[calc(var(--unit-minthr)*16)] tw-font-bold tw-tracking-[-0.01em] tw-leading-tight tw-text-dark tw-m-0 tw-flex tw-items-center tw-gap-2">
                {t('chat.team.chat', {
                  defaultValue: "Conversations d'équipe",
                })}
                {totalCount !== null && (
                  <span className="tw-inline-flex tw-items-center tw-px-1.5 tw-py-0 tw-rounded-full tw-bg-primary-opacity tw-text-primary tw-text-[calc(var(--unit-minthr)*10)] tw-font-semibold tw-tracking-normal tw-leading-[calc(var(--unit-minthr)*16)]">
                    {totalCount}
                  </span>
                )}
              </h2>

              {/* Close button */}
              <div className="tw-absolute tw-top-2.5 tw-right-2.5 tw-flex tw-items-center tw-gap-1">
                <button
                  type="button"
                  onClick={() => closeSidebar(IndexChat)}
                  title={t('close', { defaultValue: 'Close' })}
                  aria-label={t('close', { defaultValue: 'Close' })}
                  className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-border-none tw-bg-transparent tw-text-dark-gray3 hover:tw-bg-light-gray2 tw-cursor-pointer tw-transition"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>

              {/* Segmented tabs */}
              <div
                className="tw-mt-2.5 tw-grid tw-grid-cols-2 tw-gap-1 tw-bg-[#f6f8f7] tw-p-0.5 tw-rounded-lg tw-border tw-border-gray3"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isGroup}
                  onClick={() => setIsGroup(false)}
                  className={`tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-2 tw-py-1.5 tw-text-[calc(var(--unit-minthr)*12)] tw-font-semibold tw-rounded-md tw-border-none tw-cursor-pointer tw-transition ${
                    !isGroup
                      ? 'tw-bg-white tw-text-primary'
                      : 'tw-bg-transparent tw-text-[#98a29c] hover:tw-text-dark'
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21a8 8 0 0 1 16 0" />
                  </svg>
                  <span>{t('chat.Collaboraters')}</span>
                  {directCount !== null && (
                    <span
                      className={`tw-inline-grid tw-place-items-center tw-min-w-[calc(var(--unit-minthr)*16)] tw-h-[calc(var(--unit-minthr)*14)] tw-px-1 tw-rounded-full tw-text-[calc(var(--unit-minthr)*9)] tw-font-bold ${
                        !isGroup
                          ? 'tw-bg-primary-opacity tw-text-primary'
                          : 'tw-bg-[#ddeae4] tw-text-dark-gray2'
                      }`}
                    >
                      {directCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isGroup}
                  onClick={() => setIsGroup(true)}
                  className={`tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-2 tw-py-1.5 tw-text-[calc(var(--unit-minthr)*12)] tw-font-semibold tw-rounded-md tw-border-none tw-cursor-pointer tw-transition ${
                    isGroup
                      ? 'tw-bg-white tw-text-primary'
                      : 'tw-bg-transparent tw-text-[#98a29c] hover:tw-text-dark'
                  }`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="9" r="3.5" />
                    <circle cx="17" cy="10" r="2.5" />
                    <path d="M3 20a6 6 0 0 1 12 0" />
                    <path d="M14 20a5 5 0 0 1 7-4" />
                  </svg>
                  <span>
                    {t('chat.tab.groups', { defaultValue: 'Groups' })}
                  </span>
                  {groupsCount !== null && (
                    <span
                      className={`tw-inline-grid tw-place-items-center tw-min-w-[calc(var(--unit-minthr)*16)] tw-h-[calc(var(--unit-minthr)*14)] tw-px-1 tw-rounded-full tw-text-[calc(var(--unit-minthr)*9)] tw-font-bold ${
                        isGroup
                          ? 'tw-bg-primary-opacity tw-text-primary'
                          : 'tw-bg-[#ddeae4] tw-text-dark-gray2'
                      }`}
                    >
                      {groupsCount}
                    </span>
                  )}
                </button>
              </div>
            </header>

            {/* === Search row === */}
            <div className="tw-px-[calc(var(--unit-minthr)*18)] tw-pt-0.5 tw-pb-2.5">
              <div className="tw-relative">
                <div className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none tw-flex tw-items-center tw-z-50 tw-text-gray">
                  <LucideIcon
                    name="Search"
                    className="tw-w-3 tw-h-3 tw-icon-gray3"
                  />{' '}
                </div>
                <input
                  id="searchInput"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('chat.search.placeholder', {
                    defaultValue: 'Search people or conversations',
                  })}
                  className="tw-w-full tw-h-8 tw-rounded-md tw-border tw-border-gray3 tw-bg-[#f6f8f7] tw-pl-8 tw-pr-8 tw-text-[calc(var(--unit-minthr)*12.5)]  tw-outline-none focus:tw-border-primary tw-transition tw-placeholder:tw-text-light-gray4"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label={t('chat.search.clear', {
                      defaultValue: 'Clear search',
                    })}
                    className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-dark-gray4 hover:tw-bg-light-gray2 hover:tw-text-dark tw-transition"
                  >
                    <LucideIcon name="X" className="tw-w-3 tw-h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* === List === */}
            <div className="tw-flex-1 tw-min-h-0 tw-overflow-hidden tw-flex tw-flex-col ">
              <GroupeList
                refresh={refresh}
                onSelectUser={onSelectUser}
                isGroup={isGroup}
                setRefresh={setRefresh}
                externalSearch={isGroup ? search : undefined}
                onCount={setGroupsCount}
                activeId={lastOpenedId}
              />
              <UserList
                onSelectUser={onSelectUser}
                isGroup={isGroup}
                externalSearch={!isGroup ? search : undefined}
                onCount={setDirectCount}
                activeId={lastOpenedId}
              />
            </div>
          </>
        ) : loadingConversation ? (
          /* === Conversation loading === */
          <ConversationSkeleton isGroup={isGroup} />
        ) : (
          /* === Conversation view === */
          <div className="tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-bg-[#f6f8f7] active">
            <MainChatContent
              setRefresh={setRefresh}
              headerInfo={headerInfo}
              isGroup={isGroup}
              setHeaderInof={setHeaderInof}
              refresh={refresh}
              resetGroupDetailsKey={resetGroupDetailsKey}
              onBack={handleBackToList}
            />
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default IndexChat;
